import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import OnboardingPostProfileSteps from './onboarding-post-profile-steps.component';
import OnboardingStepper from './onboarding-stepper.component';
import { AiOutlineClose } from 'react-icons/ai';
import { GenericModal } from '..';
import { CompleteProfileModalProps, CompleteProfileFormData, CompleteProfileFormErrors } from './types';
import { submitUserOnboarding } from '../../../features/signup/signup.slice';
import { getUserOnboarding } from '../../../../redux/auth/authSlice';
import {  renderCountryOptionsWithI18n } from '../../../utils/countries';
import './complete-profile-modal.style.scss';
import { useTranslation } from 'react-i18next';
import { getItem } from '../../../helper/localstorage.helper';

interface RootState {
  auth: {
    user: {
      userId: number;
      firstName: string;
      lastName: string;
      email: string;
      phoneNo: string;
      address: string;
      zipCode: string;
      country: string;
      accountType: number;
      [key: string]: any;
    } | null;
  };
}

const CompleteProfileModal: React.FC<CompleteProfileModalProps> = ({
  show,
  onClose,
  onNext,
  onSubmit,
  onStartTour,
  isFromInvoice = false,
  initialCountry,
  initialStep,
}) => {
  const [formData, setFormData] = useState<CompleteProfileFormData>({
    personalId: '',
    noIdNumber: false,
    dateOfBirth: '',
    city: '',
    country: '',
    profession: '',
    address: '',
    postalCode: '',
    email: '',
    phoneNo: ''
  });
  const [formErrors, setFormErrors] = useState<CompleteProfileFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [DOBValue, setDOBValue] = useState('');
  const [showPostSteps, setShowPostSteps] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  /* Leaving the profile step with typed-but-unsaved fields asks first. */
  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [isSavingExit, setIsSavingExit] = useState(false);
  const [customCity, setCustomCity] = useState('');

  // Scroll-aware up/down swipe controls for the body (scrollbar is hidden).
  const bodyRef = useRef<HTMLDivElement>(null);
  const [scrollNav, setScrollNav] = useState({ up: false, down: false });
  useEffect(() => {
    if (!show) return;
    const el = bodyRef.current;
    if (!el) return;
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScroll = scrollHeight - clientHeight;
      setScrollNav({ up: scrollTop > 8, down: maxScroll > 8 && scrollTop < maxScroll - 8 });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [show]);
  const scrollBodyBy = (delta: number) =>
    bodyRef.current?.scrollBy({ top: delta, behavior: 'smooth' });

  const user = useSelector((state: RootState) => state.auth.user);
  /* The server's copy of this form. `auth.user` is written at login and is not
     refreshed when onboarding is saved, so on a second visit it still says the
     fields are empty — this is what actually carries a half-finished profile
     back into the boxes. */
  const userOnboardingData = useSelector((state: any) => state.auth?.userOnboardingData);
  const localstorageUser = JSON.parse(getItem('user') as string)
  const isLoginWithBankId = localstorageUser?.isLoginWithBankId || false;

  // Email field is editable when: no email fetched, OR email starts with 'guest', OR email is from invozio.com
  const isEmailEditable =
    !user?.email ||
    user.email.toLowerCase().startsWith('guest') ||
    user.email.toLowerCase().includes('invozio.com');

  // Phone field is editable only when user has no phone number saved
  const isPhoneEditable = !user?.phoneNo || user.phoneNo.trim() === '';

  // Profile is "already complete" when the server data already satisfies every
  // required field. In that case the step is shown checked and the button is
  // "Next" (no API); otherwise it's "Save & Next" (API call). Any edit marks it
  // dirty so a save happens again.
  const isProfileAlreadyComplete =
    !!user && !isEmailEditable && !isPhoneEditable &&
    !!user.city && !!user.country && !!user.profession &&
    !!user.address && !!user.zipCode;
  const needsSave = !isProfileAlreadyComplete || isDirty;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  /* Pull the saved onboarding data every time the modal opens. Nothing else
     refreshes it once the dashboard has loaded, so a profile saved earlier in
     the session (or in another tab) was not in the store when the form asked
     for it, and the boxes came up empty even though the server had the values
     — which is also why the dashboard card could read the step as done while
     this form looked blank. The prefill below never overwrites what is being
     typed, so a late answer is safe. */
  useEffect(() => {
    if (!show) return;
    (dispatch as any)(getUserOnboarding());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Deep-link from the dashboard card: jump straight into the post-profile
  // steps when the target is Bank Details, Email verification, or ID verification.
  useEffect(() => {
    if (show && (initialStep === 'bank' || initialStep === 'email' || initialStep === 'verify') && !isFromInvoice) {
      setShowPostSteps(true);
    }
  }, [show, initialStep, isFromInvoice]);

  const firstFilled = (...values: (string | undefined | null)[]) =>
    values.find(v => typeof v === 'string' && v.trim() !== '') || '';

  const isPlaceholderEmail = (email?: string) =>
    !email || email.toLowerCase().startsWith('guest') || email.toLowerCase().includes('invozio.com');

  /* What this form looks like with nothing typed. It seeds the boxes AND is the
     yardstick for "has anything changed?", so the two can never disagree.

     Once the onboarding record has loaded it IS the truth for these fields —
     an empty one included, because that is what a field the user cleared looks
     like. `auth.user` is written at login and never refreshed afterwards, so
     reading it first resurrected values that had just been removed: the save
     went through, the record came back empty, and the stale copy put the old
     value straight back in the box. It is only a fallback for the frame before
     the record arrives. */
  const savedBaseline = useMemo(() => {
    const saved: any = userOnboardingData;
    const fromRecord = (key: string, fallback?: string) =>
      saved ? (saved[key] || '') : (fallback || '');

    return {
      // A guest/invozio placeholder is not a real address — leave the box empty
      // for the user, unless a real one was saved on an earlier visit.
      email: isEmailEditable
        ? (isPlaceholderEmail(saved?.email) ? '' : firstFilled(saved?.email))
        : firstFilled(user?.email, saved?.email),
      country:    firstFilled(initialCountry) || fromRecord('country', user?.country),
      city:       fromRecord('city', user?.city),
      address:    fromRecord('address', user?.address),
      postalCode: fromRecord('zipCode', user?.zipCode),
      profession: fromRecord('profession', user?.profession),
      phoneNo:    fromRecord('phoneNo', user?.phoneNo),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOnboardingData, user, initialCountry, isEmailEditable]);

  /* Whatever is already in the box wins: this also runs when the saved data
     arrives mid-session, and it must never overwrite what is being typed. */
  useEffect(() => {
    if (show) {
      setFormData(prev => ({
        ...prev,
        email:      firstFilled(prev.email, savedBaseline.email),
        country:    firstFilled(prev.country, savedBaseline.country),
        city:       firstFilled(prev.city, savedBaseline.city),
        address:    firstFilled(prev.address, savedBaseline.address),
        postalCode: firstFilled(prev.postalCode, savedBaseline.postalCode),
        profession: firstFilled(prev.profession, savedBaseline.profession),
        phoneNo:    firstFilled(prev.phoneNo, savedBaseline.phoneNo),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, savedBaseline]);

  /* Real edits only. `isDirty` flips on the first keystroke and never flips
     back, so typing a digit and deleting it — or simply reopening the modal —
     would still have counted as unsaved work. This compares what is in the
     boxes with what is already stored. */
  const hasUnsavedChanges =
    (['email', 'country', 'city', 'address', 'postalCode', 'profession', 'phoneNo'] as const)
      .some(field => (formData[field] || '').trim() !== (savedBaseline[field] || '').trim())
    || (formData.city === 'other' && !!customCity.trim());

  const resetForm = () => {
    setFormData({
      personalId: '',
      noIdNumber: false,
      dateOfBirth: '',
      city: '',
      country: '',
      profession: '',
      address: '',
      postalCode: '',
      email: '',
      phoneNo: ''
    });
    setFormErrors({});
    setDOBValue('');
    setCustomCity('');
    setIsDirty(false);
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setIsDirty(true);
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      if (field === 'noIdNumber' && !value) {
        newData.dateOfBirth = '';
      }
      return newData;
    });

    if (formErrors[field as keyof CompleteProfileFormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const fields = ['city', 'country', 'profession', 'email', 'address', 'postalCode'] as const;
    const messages = ['CITY_REQUIRED', 'COUNTRY_REQUIRED', 'PROFESSION_REQUIRED', 'EMAIL_REQUIRED', 'ADDRESS_REQUIRED', 'POSTAL_CODE_REQUIRED'];

    const errors: CompleteProfileFormErrors = {};

    fields.forEach((field, i) => {
      if (field === 'city' && formData.city === 'other') {
        if (!customCity?.trim()) errors[field] = messages[i];
      } else if (!formData[field]?.trim()) {
        errors[field] = messages[i];
      }
    });

    // Phone is required only when the field is editable (user has no saved phone),
    // and must be numeric (digits, optionally with + - spaces parentheses).
    if (isPhoneEditable) {
      const phone = formData.phoneNo?.trim() || '';
      if (!phone) {
        errors.phoneNo = 'PHONE_NUMBER_REQUIRED';
      } else if (!/^[0-9+\-\s()]+$/.test(phone) || !/\d/.test(phone)) {
        errors.phoneNo = 'PHONE_NUMBER_INVALID';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) {
      toast.error(t('PLEASE_FILL_ALL_REQUIRED_FIELDS') as string);
      return;
    }

    if (!user) {
      toast.error(t('USER_NOT_FOUND') as string);
      return;
    }

    // Already complete and unchanged → advance without any API call.
    if (!needsSave) {
      if (isFromInvoice) {
        onClose();
      } else {
        setShowPostSteps(true);
      }
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        // Required by API — include all fields from UserOnboardingPayload
        personalId:                 user?.personalId              || user?.ssn || '',
        companyRegistrationNumber:  user?.companyRegistrationNumber || '',
        vatNumber:                  user?.vatNumber               || '',
        ReferredByCode:             user?.ReferredByCode          || '',
        ReferredByUserId:           user?.ReferredByUserId        || null,
        // User identity
        firstName:   user?.firstName,
        email:       formData.email   || user.email,
        address:     formData.address || user.address,
        accountType: user.accountType,
        ssn:         user.ssn,
        zipCode:     formData.postalCode || user.zipCode,
        dateOfBirth: user.dateOfBirth || '',
        password:    user.password   || '',
        phoneNo:     formData.phoneNo || user.phoneNo || '',
        city:        formData.city === 'other' ? customCity : formData.city,
        country:     formData.country,
        profession:  formData.profession,
      };

      const response = await (dispatch as any)(submitUserOnboarding(payload as any));

      if (response.meta.requestStatus === 'fulfilled') {
        /* Same refresh as the partial save — see handleSaveAndClose. */
        (dispatch as any)(getUserOnboarding());
        toast.success(t('PROFILE_UPDATED_SUCCESSFULLY') as string);

        if (onSubmit) {
          onSubmit(formData);
        }

        if (isFromInvoice) {
          // From invoice flow: just close the modal, no post-onboarding steps
          onClose();
        } else {
          // Defer marking onboarding complete — the user must first finish the
          // Bank Details + Bank ID steps (OnboardingPostProfileSteps). Hide the
          // profile form and hand off to the post-profile step flow.
          setShowPostSteps(true);
        }
      } else {
        toast.error(response.payload || t('PROFILE_UPDATE_FAILED') as string);
      }
    } catch (error: any) {
      console.error('Profile update error:', error);

      if (error?.response?.data?.message) {
        toast.error(String(t(error.response.data.message as string)) || String(error.response.data.message));
      } else if (error?.message) {
        toast.error(String(t(error.message)));
      } else {
        toast.error(t('PROFILE_UPDATE_FAILED') as string);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const isFormComplete =
    formData.city?.trim() !== '' &&
    formData.country?.trim() !== '' &&
    formData.profession?.trim() !== '' &&
    formData.email?.trim() !== '' &&
    formData.address?.trim() !== '' &&
    formData.postalCode?.trim() !== '' &&
    (!isPhoneEditable || formData.phoneNo?.trim() !== '')

  // Overall onboarding progress for the bar in the footer. Profile is step 1 of
  // 3 (Profile → Bank → Verify); it counts as done once complete/ready.
  const totalSteps = 3;
  const doneSteps = (isProfileAlreadyComplete || isFormComplete) ? 1 : 0;
  const progressPct = Math.round((doneSteps / totalSteps) * 100);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  /* The close (X) button only. Back and Save & Next are deliberate moves
     within the flow and keep their original behaviour; this asks before the
     one action that means "I'm leaving" while fields are half filled. Also
     only this step — the invoice flow closes the way it always did, and the
     post-profile steps have their own close. */
  const requestClose = () => {
    if (!isFromInvoice && !showPostSteps && hasUnsavedChanges) {
      setShowExitPrompt(true);
      return;
    }
    handleClose();
  };

  /* Saves what has been typed so far and closes. Same endpoint the step's own
     "Save & Next" uses — the form reads its values back from `user` when it
     reopens, so this is what makes "continue later" actually continue.

     Unlike handleNext's payload, every field falls back to the stored value:
     this runs on a half-filled form, and a blank box must never overwrite
     something the account already has. */
  const handleSaveAndClose = async () => {
    if (!user) {
      toast.error(t('USER_NOT_FOUND') as string);
      return;
    }

    /* What to send for a field. `savedBaseline` is what the boxes were seeded
       with, so a box that differs from it was changed by hand — including
       being emptied on purpose, which the previous `formData.x || user.x`
       quietly undid. A box that matches sends the stored value unchanged, so a
       form saved before the data finished loading still cannot blank
       anything. */
    const valueFor = (key: keyof typeof savedBaseline, current?: string): string =>
      (current || '').trim() !== (savedBaseline[key] || '').trim()
        ? (current || '')
        : (savedBaseline[key] || '');

    setIsSavingExit(true);
    try {
      const payload = {
        personalId:                user?.personalId              || user?.ssn || '',
        companyRegistrationNumber: user?.companyRegistrationNumber || '',
        vatNumber:                 user?.vatNumber               || '',
        ReferredByCode:            user?.ReferredByCode          || '',
        ReferredByUserId:          user?.ReferredByUserId        || null,
        firstName:   user?.firstName,
        /* Email keeps a fallback of its own: it is the account's login, and an
           empty box here (the guest-placeholder case) must never clear it. */
        email:       formData.email      || user.email,
        address:     valueFor('address', formData.address),
        accountType: user.accountType,
        ssn:         user.ssn,
        zipCode:     valueFor('postalCode', formData.postalCode),
        dateOfBirth: user.dateOfBirth    || '',
        password:    user.password       || '',
        phoneNo:     valueFor('phoneNo', formData.phoneNo),
        city:        valueFor('city', formData.city === 'other' ? customCity : formData.city),
        country:     valueFor('country', formData.country),
        profession:  valueFor('profession', formData.profession),
      };

      const response = await (dispatch as any)(submitUserOnboarding(payload as any));

      if (response.meta.requestStatus === 'fulfilled') {
        /* Pull the saved values back into the store before closing: nothing else
           refreshes them, and they are what fills the form on the next visit. */
        await (dispatch as any)(getUserOnboarding());
        toast.success(t('ONBOARDING_PROGRESS_SAVED') as string);
        setShowExitPrompt(false);
        handleClose();
      } else {
        /* Nothing was lost — drop back into the form with the reason. */
        toast.error(response.payload || t('PROFILE_UPDATE_FAILED') as string);
        setShowExitPrompt(false);
      }
    } catch (error: any) {
      console.error('Onboarding progress save error:', error);
      toast.error(t('PROFILE_UPDATE_FAILED') as string);
      setShowExitPrompt(false);
    } finally {
      setIsSavingExit(false);
    }
  };

  return (
    <div className='complete-profile-container-main'> 
      {/* Hidden while the confirmation is up, so only one dialog is ever on
          screen. The form's state lives in this component, not in the modal,
          so nothing typed is lost while it is away — Cancel brings it back
          exactly as it was. */}
      <GenericModal
        show={show && !showExitPrompt}
        onClose={handleClose}
        size="xl"
        showFooter={false}
        showCloseButton={false}
        closeOnBackdrop={true}
        className="complete-profile-modal"
      >
        <div className="complete-profile-content">
          {showPostSteps && !isFromInvoice ? (
            /* Same modal stays open — body swaps to the post-profile steps */
            <OnboardingPostProfileSteps
              isLoginWithBankId={isLoginWithBankId}
              initialStep={initialStep === 'bank' || initialStep === 'email' || initialStep === 'verify' ? initialStep : undefined}
              onBack={() => setShowPostSteps(false)}
              onStartTour={() => { setShowPostSteps(false); onStartTour(); }}
              onComplete={() => { setShowPostSteps(false); onNext ? onNext() : onClose(); }}
            />
          ) : (
          <>
          {/* Header — in the onboarding flow the title is dropped so only the
              stepper leads the modal; the close button stays (floated right).
              The invoice flow keeps its titled header unchanged. */}
          <div className={`complete-profile-header${isFromInvoice ? '' : ' complete-profile-header--minimal'}`}>
            {isFromInvoice && <h2 className="complete-profile-title">{t('COMPLETE_PROFILE')}</h2>}
            <button
              className="complete-profile-close-btn"
              onClick={requestClose}
              aria-label="Close"
              disabled={isLoading}
            >
              <AiOutlineClose size={32} />
            </button>
          </div>

          {/* Unified onboarding stepper */}
          {!isFromInvoice && (
            <OnboardingStepper
              active="profile"
              completed={!needsSave ? ['profile'] : []}
            />
          )}

          {/* Warning explaining what stays locked while onboarding is incomplete */}
          {!isFromInvoice && (
            <div className="cp-warning" role="alert">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p>{t('ONBOARDING_INCOMPLETE_WARNING')}</p>
            </div>
          )}

          {/* Body wrapped so the swipe up/down hints float over its top/bottom
              edges — same control the registration form uses. Each hint shows
              only while there is more content that way. */}
          <div className="cp-scroll-wrap">
          <div className={`cp-swipe-hint cp-swipe-hint--up${scrollNav.up ? ' is-visible' : ''}`}>
            <button
              type="button"
              className="cp-swipe-hint__pill"
              onClick={() => scrollBodyBy(-Math.round((bodyRef.current?.clientHeight || 260) * 0.85))}
              tabIndex={scrollNav.up ? 0 : -1}
              aria-label={String(t('PARTNER_SWIPE_UP'))}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 10l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              {t('PARTNER_SWIPE_UP')}
            </button>
          </div>

          <div className="complete-profile-body" ref={bodyRef}>
            <div className="form-section">
              {/* Email + Phone share one row (two columns); they stack on mobile
                  via the existing .form-row responsive rule. */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('EMAIL')}</label>
                  {formErrors.email && (
                    <span className="error-message">{t(formErrors.email)}</span>
                  )}
                  <input
                    type="email"
                    className="form-input py-0 my-0"
                    placeholder={t('ENTER_EMAIL') || 'Enter your email'}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={isLoading || !isEmailEditable}
                    style={{ marginTop: '8px' }}
                  />
                </div>
              <div className="form-group">
                <label className="form-label">{t('PHONE_NUMBER')}</label>
                {formErrors.phoneNo && (
                  <span className="error-message">{t(formErrors.phoneNo)}</span>
                )}
                <input
                  type="tel"
                  inputMode="tel"
                  className="form-input py-0 my-0"
                  placeholder={t('ENTER_PHONE_NUMBER') || 'Enter your phone number'}
                  value={formData.phoneNo}
                  onChange={(e) => {
                    // Numbers only — block letters/words (allow +, spaces, hyphens, parentheses)
                    const sanitizedValue = e.target.value.replace(/[^0-9+\-\s()]/g, '');
                    handleInputChange('phoneNo', sanitizedValue);
                  }}
                  disabled={isLoading || !isPhoneEditable}
                  style={{ marginTop: '8px' }}
                />
                </div>
              </div>

              <div className="form-row">


                <div className="form-group">
                  <label className="form-label">{t('COUNTRY')}</label>
                  <select
                    className={`form-select ${formErrors.country ? 'error' : ''}`}
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">{t('SELECT_YOUR_COUNTRY')}</option>
                    <option value={"Sweden"}>{t("SWEDEN")}</option>
                    <option value={"USA"}>{"USA"}</option>
                    {renderCountryOptionsWithI18n(t)}
                  </select>
                  {formErrors.country && (
                    <span className="error-message">{t(formErrors.country)}</span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">{t('CITY')}</label>
                  {formErrors.city && (
                    <span className="error-message">{t(formErrors.city)}</span>
                  )}
                  <input
                    type="text"
                    className="form-input py-0 my-0"
                    placeholder={t('ENTER_CITY') || 'Enter your city'}
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    disabled={isLoading}
                    style={{ marginTop: '8px' }}
                  />
                </div>
              </div>

              {/* Address and Postal Code Row - shown for all users */}
              <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('ADDRESS')}</label>
                    {formErrors.address && (
                      <span className="error-message">{t(formErrors.address)}</span>
                    )}
                    <input
                      type="text"
                      className="form-input py-0 my-0"
                      placeholder={t('ENTER_ADDRESS') || 'Enter your address'}
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      disabled={isLoading}
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('POSTAL_CODE')}</label>
                    {formErrors.postalCode && (
                      <span className="error-message">{t(formErrors.postalCode)}</span>
                    )}
                    <input
                      type="text"
                      maxLength={10}
                      className="form-input py-0 my-0"
                      placeholder={t('ENTER_POSTAL_CODE') || 'Enter your postal code'}
                      value={formData.postalCode}
                      onChange={(e) => {
                        const sanitizedValue = e.target.value.replace(/[^A-Za-z0-9\s,-]/g, '');
                        handleInputChange('postalCode', sanitizedValue);
                      }}
                      disabled={isLoading}
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                </div>

              {/* Profession */}
              <div className="form-group profession-group" style={{ width: '100%', position: 'relative' }}>
                <label className="form-label" htmlFor="profession-input">{t('PROFESSION')}</label>
                {formErrors.profession && (
                  <span className="error-message">{t(formErrors.profession)}</span>
                )}
                <input
                  id="profession-input"
                  type="text"
                  className={`form-input ${formErrors.profession ? 'error' : ''}`}
                  placeholder={t('ENTER_PROFESSION') || 'Enter your profession'}
                  value={formData.profession}
                  onChange={(e) => handleInputChange('profession', e.target.value)}
                  disabled={isLoading}
                  style={{ marginTop: '8px', position: 'relative', zIndex: 1 }}
                />
              </div>
            </div>
          </div>

          <div className={`cp-swipe-hint cp-swipe-hint--down${scrollNav.down ? ' is-visible' : ''}`}>
            <button
              type="button"
              className="cp-swipe-hint__pill"
              onClick={() => scrollBodyBy(Math.round((bodyRef.current?.clientHeight || 260) * 0.85))}
              tabIndex={scrollNav.down ? 0 : -1}
              aria-label={String(t('PARTNER_SWIPE_DOWN'))}
            >
              {t('PARTNER_SWIPE_DOWN')}
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
          </div>

          {/* Footer */}
          <div className="complete-profile-footer" style={{ justifyContent: isFromInvoice ? 'flex-end' : 'space-between' }}>
            {!isFromInvoice && (
              <button type="button" className="onboarding-back-btn" onClick={onClose}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                {t('BACK') || 'Back'}
              </button>
            )}

            {/* Setup progress — centered between Back and Save & Next */}
            {!isFromInvoice && (
              <div className="cp-progress">
                <div className="cp-progress__head">
                  <span className="cp-progress__label">{t('ONBOARDING_SETUP_PROGRESS')}</span>
                  <span className="cp-progress__count">{doneSteps}/{totalSteps} · {progressPct}%</span>
                </div>
                <div className="cp-progress__track">
                  <div className="cp-progress__fill" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            )}

            {/* Save & Next Button */}
            <button
              className={isFromInvoice ? 'profile-save-btn' : 'profile-next-btn'}
              onClick={handleNext}
              disabled={!isFormComplete || isLoading}
            >
              {isLoading
                ? t('LOADING') + '...'
                : (isFromInvoice
                    ? t('SAVE')
                    : (needsSave ? (t('SAVE_AND_NEXT') || 'Save & Next') : t('NEXT')))}
            </button>
          </div>

          </>
          )}
        </div>
      </GenericModal>

      {/* Leave-with-unsaved-changes confirmation — a modal of its own rather
          than an overlay, so it replaces the onboarding modal instead of
          stacking on top of it. Backdrop and Escape both mean "cancel". */}
      <GenericModal
        show={showExitPrompt}
        onClose={() => setShowExitPrompt(false)}
        size="sm"
        variant="centered"
        showFooter={false}
        showCloseButton={false}
        closeOnBackdrop={true}
        className="cp-exit-modal"
      >
        <div className="cp-exit__card" role="document">
          <span className="cp-exit__icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <p className="cp-exit__title">
            {t('ONBOARDING_EXIT_SAVE_TITLE') || 'Save your progress?'}
          </p>
          <p className="cp-exit__msg">
            {t('ONBOARDING_EXIT_SAVE_MSG')}
          </p>
          <div className="cp-exit__actions">
            <button
              type="button"
              className="cp-exit__btn cp-exit__btn--ghost"
              onClick={() => setShowExitPrompt(false)}
              disabled={isSavingExit}
            >
              {t('CANCEL') || 'Cancel'}
            </button>
            <button
              type="button"
              className="cp-exit__btn cp-exit__btn--primary"
              onClick={handleSaveAndClose}
              disabled={isSavingExit}
            >
              {isSavingExit ? t('LOADING') + '...' : (t('SAVE_CHANGES') || 'Save Changes')}
            </button>
          </div>
        </div>
      </GenericModal>
    </div>
  );
};

export default CompleteProfileModal;
